import type {AutocompleteItem} from '@mantine/core';
import {useRouter} from 'next/router';
import {useEffect, useState} from 'react';
import {ReportCategoryTypes} from '../../../core/entities/reports/constants';
import {convertReportCategoryTypeIdsToStrings} from '../../../core/factories/reports/convertReportCategoryTypeIdsToStrings';
import {flattenCompanyListByCategory} from '../../../core/factories/reports/flattenCompanyListByCategory';
import {
  useDepartmentMaster,
  useIndustryCategories,
  useReportsCount,
} from '../../../core/repositories/reportsRepository';

type FilterItemByDepartment = {
  reportType: number[];
  industry: number[];
};

export type UseHandleReportsByDepartmentReturnType = ReturnType<
  typeof useHandleReportsByDepartment
>;

export const useHandleReportsByDepartment = ({
  departmentId,
}: {
  departmentId: number;
}) => {
  const reportTypes = ReportCategoryTypes;
  const {data: industryCategories, isLoading: isLoadingIndustryCategories} =
    useIndustryCategories({});
  const {data: departmentMaster, isLoading: isLoadingDepartmentMaster} =
    useDepartmentMaster({
      queryParams: {
        isPopular: 'true',
      },
    });

  const [filterItems, setFilterItems] = useState<FilterItemByDepartment>({
    reportType: [],
    industry: [],
  });

  const router = useRouter();
  const {type, category, q} = router.query;
  // 明示的に文字列型として扱う
  const typeStr = type as string;
  const categoryStr = category as string;
  const qStr = q as string;

  const [searchWord, setSearchWord] = useState(qStr ?? '');

  useEffect(() => {
    if (!industryCategories?.ok || !departmentMaster?.ok) {
      return;
    }

    const reportTypeFilterItem = typeStr
      ? typeStr
          .split(',')
          .flatMap((t) =>
            ReportCategoryTypes.types
              .filter((reportType) => reportType.key === t)
              .map((reportType) => reportType.id),
          )
      : [];
    const newFilterItems: FilterItemByDepartment = {
      reportType: reportTypeFilterItem,
      industry: categoryStr
        ? categoryStr.split(',').map((id) => Number.parseInt(id, 10))
        : [],
    };
    setFilterItems(newFilterItems);
  }, [industryCategories, departmentMaster]);

  // 動的にURLを変更する
  // biome-ignore lint/correctness/useExhaustiveDependencies: filterItems 変化時のみ URL に反映したいため意図的に除外
  useEffect(() => {
    if (!router.isReady) {
      return;
    }
    const url: string = getReportUrlByDepartment(
      router.asPath.replace(/\?.*$/, ''),
      filterItems,
    );
    router.replace(url).catch((error) => {
      console.error('Failed to push the new URL', error);
    });
  }, [filterItems]);

  const {data: reportsCount, isLoading: isLoadingReportsCount} =
    useReportsCount({
      queryParams: {
        types: convertReportCategoryTypeIdsToStrings(filterItems.reportType),
        industryCategoryIDs: filterItems.industry,
        departmentMasterIDs: [departmentId],
      },
    });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [reportsCount]);

  const handleSearchInputChange = (searchWord: string) => {
    setSearchWord(searchWord);
  };

  const autoCompleteData: AutocompleteItem[] = reportsCount?.ok
    ? flattenCompanyListByCategory(reportsCount.value).map((company) => ({
        id: company.companyID,
        value: company.companyName,
      }))
    : [];

  const isLoading =
    isLoadingIndustryCategories ||
    isLoadingDepartmentMaster ||
    isLoadingReportsCount;

  return {
    industryCategories,
    departmentMaster,
    reportTypes,
    searchWord,
    setSearchWord,
    filterItems,
    setFilterItems,
    reportsCount,
    autoCompleteData,
    handleSearchInputChange,
    isLoading,
  };
};

const getReportUrlByDepartment = (
  path: string,
  filterItems: FilterItemByDepartment,
): string => {
  const types = filterItems.reportType.map((type) => {
    return ReportCategoryTypes.types.find((t) => t.id === type)?.key;
  });
  const industries = filterItems.industry;
  const url = `${path}?type=${
    types ? (Array.isArray(types) ? types.join(',') : types) : ''
  }&category=${
    industries
      ? Array.isArray(industries)
        ? industries.join(',')
        : industries
      : ''
  }`;

  return url;
};
